/**
 * Renders a legal document (Terms / Privacy / Refund) from its Markdown source.
 * The Markdown is the single source of truth in src/content/legal/*.md, imported
 * with Vite's `?raw`, so the served page never drifts from the reviewed text.
 *
 * Branded as part of 4K Labs (logo header + footer), scrolls inside the app shell
 * (which is overflow-hidden), and rewrites the cross-document `.md` links to their
 * live routes so they don't 404.
 */
import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Logo from '@/components/ui/logo';

// The Markdown cross-links point at sibling files (./privacy-policy.md). Map them
// to the live SPA routes so clicking them navigates instead of 404-ing.
const MD_TO_ROUTE: Record<string, string> = {
  './terms-of-service.md': '/terms',
  './privacy-policy.md': '/privacy',
  './refund-policy.md': '/refund',
  'terms-of-service.md': '/terms',
  'privacy-policy.md': '/privacy',
  'refund-policy.md': '/refund',
};

function MarkdownLink({ href, children }: { href?: string; children?: ReactNode }) {
  const mapped = href ? MD_TO_ROUTE[href] ?? href : undefined;
  const isExternal = !!mapped && /^https?:\/\//.test(mapped);
  return (
    <a href={mapped} {...(isExternal ? { target: '_blank', rel: 'noreferrer' } : {})}>
      {children}
    </a>
  );
}

export function LegalDoc({ content }: { content: string }) {
  return (
    <div className="h-full overflow-y-auto bg-background">
      {/* Branded header */}
      <header className="sticky top-0 z-10 border-b bg-background/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-3xl items-center justify-between px-6">
          <a href="/" aria-label="4K Labs home" className="flex items-center">
            <Logo />
          </a>
          <nav className="flex items-center gap-5 text-sm text-muted-foreground">
            <a href="/terms" className="hover:text-foreground">Terms</a>
            <a href="/privacy" className="hover:text-foreground">Privacy</a>
            <a href="/refund" className="hover:text-foreground">Refund</a>
          </nav>
        </div>
      </header>

      <article className="prose prose-neutral dark:prose-invert mx-auto max-w-3xl px-6 py-10">
        {/* GFM: the policies carry tables (sub-processors, website cookies). */}
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: MarkdownLink }}>{content}</ReactMarkdown>
      </article>

      {/* Footer */}
      <footer className="mx-auto max-w-3xl border-t px-6 py-8 text-xs text-muted-foreground">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} 4K Labs Inc</span>
          <span className="flex items-center gap-4">
            <a href="/terms" className="hover:text-foreground">Terms</a>
            <a href="/privacy" className="hover:text-foreground">Privacy</a>
            <a href="/refund" className="hover:text-foreground">Refund</a>
            <a href="mailto:legal@4klabs.ai" className="hover:text-foreground">legal@4klabs.ai</a>
          </span>
        </div>
      </footer>
    </div>
  );
}
